import { CropFree as CropFreeIcon } from "@mui/icons-material";
import {
  Autocomplete,
  CircularProgress,
  FilterOptionsState,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import { unwrapResult } from "@reduxjs/toolkit";
import { BarcodeScannerDialog } from "components/barcodeScan";
import { ScannerPartSuggestDialog } from "components/barcodeScan/ScannerPartSuggestDialog";
import StyledTextField from "components/StyledTextField";
import { scanditLicense } from "env";
import { filterPartList, intlFormatPrice } from "helpers";
import technicianSettings from "helpers/technicianSettings";
import { useDebounce } from "hooks/useDebounce";
import { StockLocationType } from "models/StockLocationType";
import { nanoid } from "nanoid";
import { PartType, StockStore } from "operations/schema/schema";
import { FC, useCallback, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useAppDispatch, useAppSelector } from "store";
import { selectConnectionStatus } from "store/root.store";
import { getAvailableParts } from "store/slices/cache.store";
import { addSnackbarMessage } from "store/slices/snackbar.store";

export interface PartAutocompleteProps {
  stockLocation: StockLocationType;
  loading: boolean;
  showQty: boolean;
  partList: PartType[];
  partSearch: string;
  setPartSearch: React.Dispatch<React.SetStateAction<string>>;
  onChange: (selectedPart: PartType | null) => void;
  disableBarcodeScan?: boolean;
}

export const PartAutocomplete: FC<PartAutocompleteProps> = (props) => {
  const {
    stockLocation,
    loading,
    showQty,
    partList,
    partSearch,
    setPartSearch,
    onChange,
    disableBarcodeScan,
  } = props;
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const isOnline = useAppSelector(selectConnectionStatus);
  const [selectedPart, setSelectedPart] = useState<PartType | null>(null);
  const [openScanner, setOpenScanner] = useState(false);
  const [openSuggested, setOpenSuggested] = useState(false);
  const [possibleMatches, setPossibleMatches] = useState<PartType[]>([]);
  const [scannedValue, setScannedValue] = useState("");
  const [partInputValue, setPartInputValue] = useState<string>("");
  const debouncedPartSearch = useDebounce<string>(partSearch, 500);
  const allowSalesPrice = technicianSettings.syncSalesPrice || technicianSettings.syncCostPrice;
  const hasBarcodeScanner = scanditLicense() && !disableBarcodeScan;

  useEffect(() => {
    dispatch(
      getAvailableParts({
        location: {
          locationId: stockLocation.locationId,
          stockStore: stockLocation.type,
        },
        searchString: stockLocation.type === StockStore.Locational ? debouncedPartSearch : "",
      }),
    );
  }, [debouncedPartSearch, dispatch, stockLocation]);

  const findMatches = useCallback(
    (parts: PartType[], scannerValue: string) => {
      const testValue = scannerValue.toLowerCase();
      let scanMatches: PartType[] = [];
      let equalMatch: PartType | null = null;
      for (const part of parts) {
        const barcode = part.barcode?.toLowerCase() ?? "";
        const articleNo = part.articleNo?.toLowerCase() ?? "";

        if (barcode === testValue || articleNo === testValue) {
          equalMatch = part;
          break;
        } else if (barcode.includes(testValue) || articleNo.includes(testValue)) {
          scanMatches.push(part);
        }
      }

      if (equalMatch !== null) {
        setSelectedPart(equalMatch);
      } else {
        if (scanMatches.length === 0) {
          dispatch(addSnackbarMessage({ key: "No-barcode-match", argument: scannerValue }));
        } else {
          setPossibleMatches(scanMatches);
          setOpenSuggested(true);
        }
      }
    },
    [dispatch],
  );

  const onScan = useCallback(
    (scannerValue: string) => {
      setScannedValue(scannerValue);
      setOpenScanner(false);
      setPartInputValue(scannerValue);

      if (stockLocation.type === StockStore.Locational && isOnline) {
        dispatch(
          getAvailableParts({
            location: {
              locationId: stockLocation.locationId,
              stockStore: stockLocation.type,
            },
            searchString: scannerValue,
          }),
        )
          .then(unwrapResult)
          .then(({ data: { parts } }) => {
            findMatches(parts, scannerValue);
          });
      } else {
        findMatches(partList, scannerValue);
      }
    },
    [dispatch, findMatches, isOnline, partList, stockLocation.locationId, stockLocation.type],
  );

  const handleCloseSuggest = useCallback(() => {
    setOpenSuggested(false);
    setScannedValue("");
  }, []);

  const onSelectPart = useCallback(
    (part: PartType | null) => {
      setSelectedPart(part);
      onChange(part);
    },
    [onChange],
  );

  return (
    <Grid item container alignItems="start" justifyContent="space-between" wrap="nowrap">
      <Autocomplete
        fullWidth
        data-testid="PartAutocomplete"
        options={partList}
        loading={loading}
        groupBy={(part) => {
          const stockStoreTranslatable =
            part.stockStore === StockStore.Engineer
              ? intl.formatMessage({ id: "general.engineer" })
              : part.stockStore === StockStore.Locational
                ? intl.formatMessage({ id: "part.locational" })
                : part.stockStore === StockStore.Nonstock
                  ? intl.formatMessage({ id: "part.nonStock" })
                  : part.stockStore === StockStore.Other
                    ? intl.formatMessage({ id: "part.other" })
                    : part.stockStore;

          return part.stockStore === StockStore.Locational
            ? (part.stockName?.toUpperCase() ?? "")
            : (stockStoreTranslatable?.toUpperCase() ?? "");
        }}
        getOptionLabel={(part: PartType) => {
          let label = part.description;

          if (showQty && !part.isNonStock) {
            label += ` (${part.quantity})`;
          }
          return label ?? "";
        }}
        noOptionsText={intl.formatMessage({
          id: "general.noOptions",
        })}
        filterOptions={(options: PartType[], state: FilterOptionsState<PartType>) => {
          return filterPartList(options, state.inputValue);
        }}
        value={selectedPart}
        inputValue={partInputValue}
        onInputChange={(_, value) => {
          setPartSearch(value);
          setPartInputValue(value);
        }}
        onChange={(_, value) => onSelectPart(value)}
        renderInput={(params) => (
          <StyledTextField
            {...params}
            label={intl.formatMessage({
              id: "part",
            })}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, part: PartType) => (
          <li {...props} key={nanoid()} style={{ display: "block" }}>
            <Typography variant="body1">
              {part.description}
              {showQty && !part.isNonStock && ` (${part.quantity})`}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {part.articleNo}
            </Typography>
            {allowSalesPrice && part.salesPrice && (
              <Typography variant="body2" color="textSecondary">
                <FormattedMessage id="general.salesPrice" />:{" "}
                {intlFormatPrice(intl, part.salesPrice)}
              </Typography>
            )}
          </li>
        )}
      />
      {hasBarcodeScanner && (
        <>
          <Grid item>
            <IconButton
              data-testid="PartAutocomplete-BarcodeButton"
              onClick={() => setOpenScanner(true)}
              size="large"
              sx={{ pr: 0, ml: 1 }}
            >
              <CropFreeIcon fontSize="large" />
            </IconButton>
          </Grid>

          <BarcodeScannerDialog
            open={openScanner}
            setOpen={(open: boolean) => setOpenScanner(open)}
            submitValue={onScan}
          />

          <ScannerPartSuggestDialog
            open={openSuggested}
            scannerValue={scannedValue}
            parts={possibleMatches}
            selectPart={onSelectPart}
            onClose={handleCloseSuggest}
          />
        </>
      )}
    </Grid>
  );
};
